import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Image as ImageIcon, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { profilesAPI, kpiV2API } from '../../services/api';
import { resolvePhotoUrl } from '../../utils/photoUtils';
import { QrUploadButton, appendCapped } from './workspace/QrUpload';
import { fmtMD, fmtMDHM, toDate, toLocalInput, fromLocalInput, memoPresetAt, asArray } from '../service/sopShared';
import RecPager, { pageSlice } from './workspace/RecPager';

// 备注记录 tab of the CRM modal (VIP 服务流程 5.0). A note is a plain 备注
// unless 设为待办 is ticked — then it carries a reminder time and an assignee,
// shows up in 今日跟进 when due, and is closed with 完成 (optional result).
// Without the 待办 fields from the backend every row simply reads as 备注, so
// the tab works the same as before on an older API.
//
// Props: profileId, profile (for the assignee options), meId, meName, isZh,
// focusKey (bump to focus the composer), onChanged.

const C = { ink: '#111111', sub: '#6E685F', mute: '#6E685F', line: 'rgba(17,17,17,.12)', soft: 'rgba(17,17,17,.08)', field: 'rgba(17,17,17,.12)', brand: '#111111', ok: '#3F7D5A' };
const TAG = {
  overdue: { zh: '逾期', en: 'Overdue', bg: '#F3E3E1', color: '#9E2B25' },
  open: { zh: '待办', en: 'To-do', bg: '#EFEAE2', color: '#8A6D3B' },
  done: { zh: '已完成', en: 'Done', bg: '#E6EEE8', color: '#3F7D5A' },
  note: { zh: '情况', en: 'Note', bg: '#EFEAE2', color: '#6E685F' },
};

const stateOf = (n) => {
  if (n.state && TAG[n.state]) return n.state;
  if (!n.remind_at) return 'note';
  if (n.done_at) return 'done';
  const d = toDate(n.remind_at);
  return d && d.getTime() < Date.now() ? 'overdue' : 'open';
};

// 提醒时间 presets.
// 明天 10:00 / 下周二 10:00 are Pacific (sopShared.memoPresetAt).
const presetAt = memoPresetAt;

export default function CrmNotesPanel({ profileId, profile, meId, isZh = true, focusKey = 0, onChanged }) {
  const t = (zh, en) => (isZh ? zh : en);
  const [rows, setRows] = useState(null);
  const [authors, setAuthors] = useState([]);
  const [author, setAuthor] = useState('');
  const [text, setText] = useState('');
  const [todo, setTodo] = useState(false);
  const [preset, setPreset] = useState('tomorrow');
  const [custom, setCustom] = useState('');
  const [assignee, setAssignee] = useState('');
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [doneFor, setDoneFor] = useState(null);   // note id with the 完成 box open
  const [doneText, setDoneText] = useState('');
  const [editing, setEditing] = useState(null);   // { id, text }
  const [busyId, setBusyId] = useState(null);
  const [page, setPage] = useState(1);
  const fileRef = useRef(null);
  const textRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await profilesAPI.getProfileNotes(profileId);
      const d = r.data || {};
      setRows(Array.isArray(d.notes) ? d.notes : Array.isArray(d.items) ? d.items : []);
      setAuthors(Array.isArray(d.authors) ? d.authors : []);
    } catch (e) {
      setRows([]);
    }
  }, [profileId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (focusKey && textRef.current) textRef.current.focus(); }, [focusKey]);

  // 负责人 options: me, the member's coach / matchmaker, and anyone who has
  // written on this member — no staff directory endpoint is needed.
  const assignees = useMemo(() => {
    const out = [];
    const add = (id, name) => {
      const n = Number(id);
      if (!n || out.some((x) => x.id === n)) return;
      out.push({ id: n, name: name || `#${n}` });
    };
    add(meId, t('我', 'Me'));
    const p = profile || {};
    add(p.assigned_coach_id, [p.coach_first_name, p.coach_last_name].filter(Boolean).join(' '));
    add(p.matchmaker_id, [p.matchmaker_first_name, p.matchmaker_last_name].filter(Boolean).join(' '));
    authors.forEach((a) => add(a.id, a.name));
    (rows || []).forEach((n) => { add(n.created_by, n.created_by_name); add(n.assignee_user_id, n.assignee_name); });
    return out;
  }, [meId, profile, authors, rows, isZh]);

  const authorOptions = useMemo(() => {
    if (authors.length) return authors.map((a) => ({ id: String(a.id), name: a.name || `#${a.id}` }));
    const m = new Map();
    (rows || []).forEach((n) => { if (n.created_by != null && !m.has(String(n.created_by))) m.set(String(n.created_by), n.created_by_name || `#${n.created_by}`); });
    return [...m.entries()].map(([id, name]) => ({ id, name }));
  }, [authors, rows]);

  const list = useMemo(() => {
    const all = (rows || []).map((n) => ({ ...n, _state: stateOf(n), _images: asArray(n.images) }));
    const shown = author ? all.filter((n) => String(n.created_by) === author) : all;
    const rank = { overdue: 0, open: 1, done: 2, note: 2 };
    return shown.sort((a, b) => {
      const r = rank[a._state] - rank[b._state];
      if (r) return r;
      if (a._state === 'overdue' || a._state === 'open') return (toDate(a.remind_at)?.getTime() || 0) - (toDate(b.remind_at)?.getTime() || 0);
      return (toDate(b.created_at)?.getTime() || 0) - (toDate(a.created_at)?.getTime() || 0);
    });
  }, [rows, author]);
  useEffect(() => { setPage(1); }, [author]);
  const pg = pageSlice(list, page);
  const openCount = (rows || []).filter((n) => ['open', 'overdue'].includes(stateOf(n))).length;

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (images.length + files.length > 9) { toast.error(t('最多 9 张图片', 'Up to 9 images')); }
    setUploading(true);
    try {
      const up = [];
      for (const f of files.slice(0, Math.max(0, 9 - images.length))) {
        const r = await kpiV2API.uploadFile(f);
        const u = r.data?.url || r.data?.file_url;
        if (u) up.push(u);
      }
      setImages((prev) => [...prev, ...up]);
    } catch (err) {
      toast.error(t('图片上传失败', 'Upload failed'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const note = text.trim();
    if (!note) { toast.error(t('请先填写内容', 'Write something first')); return; }
    const data = { note };
    if (images.length) data.images = images;
    if (todo) {
      const at = preset === 'custom' ? fromLocalInput(custom) : presetAt(preset);  // typed as Pacific
      if (!at) { toast.error(t('请选择提醒时间', 'Pick a reminder time')); return; }
      data.remind_at = at.toISOString();
      data.assignee_user_id = Number(assignee) || Number(meId) || null;
    }
    setSaving(true);
    try {
      await profilesAPI.createProfileNote(profileId, data);
      toast.success(todo ? t('已记录待办', 'To-do saved') : t('已记录', 'Saved'));
      setText(''); setImages([]); setTodo(false); setPreset('tomorrow'); setCustom('');
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.error || t('保存失败', 'Could not save'));
    } finally { setSaving(false); }
  };

  const act = async (id, fn, ok) => {
    setBusyId(id);
    try {
      await fn();
      if (ok) toast.success(ok);
      await load();
      onChanged && onChanged();
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.error || t('操作失败', 'Failed'));
      return false;
    } finally { setBusyId(null); }
  };

  const markDone = async (n) => {
    const ok = await act(n.id, () => profilesAPI.markNoteDone(profileId, n.id, doneText.trim()), t('已完成', 'Done'));
    if (ok) { setDoneFor(null); setDoneText(''); }
  };

  const field = { font: 'inherit', fontSize: 13, border: `1px solid ${C.field}`, borderRadius: 2, padding: '5px 8px', background: '#FFFDF9', color: C.ink };
  const smallBtn = { height: 28, padding: '0 12px', borderRadius: 2, border: `1px solid ${C.field}`, background: '#FFFDF9', font: 'inherit', fontSize: 13, flexShrink: 0, cursor: 'pointer' };
  const linkBtn = { border: 'none', background: 'none', padding: '0 4px', font: 'inherit', fontSize: 12, fontWeight: 500, cursor: 'pointer' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 4 }}>
      <form onSubmit={submit} aria-label={t('写客户情况', 'Write a client note')}
        style={{ border: `1px solid ${C.line}`, borderRadius: 2, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 9 }}>
        <textarea ref={textRef} rows={2} value={text} onChange={(e) => setText(e.target.value)}
          aria-label={t('客户情况内容', 'Note')}
          placeholder={t('写客户情况：会员近况、要做的事…', 'Write a note: what happened, what needs doing…')}
          style={{ font: 'inherit', fontSize: 14, border: 'none', padding: '2px 0', resize: 'vertical', outline: 'none', color: C.ink }} />
        {images.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {images.map((u, i) => (
              <div key={u + i} style={{ position: 'relative', width: 52, height: 52, borderRadius: 2, overflow: 'hidden', border: `1px solid ${C.field}` }}>
                <img src={resolvePhotoUrl(u) || u} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <button type="button" aria-label={t('移除图片', 'Remove image')} onClick={() => setImages((p) => p.filter((_, j) => j !== i))}
                  style={{ position: 'absolute', top: 2, right: 2, width: 16, height: 16, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,.6)', color: '#FFFDF9', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={10} /></button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', borderTop: `1px solid ${C.soft}`, paddingTop: 9 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ display: 'inline-flex', gap: 7, alignItems: 'center', fontSize: 13.5, fontWeight: 500, cursor: 'pointer' }}>
              <input type="checkbox" checked={todo} onChange={(e) => setTodo(e.target.checked)} style={{ width: 16, height: 16, accentColor: C.brand }} />
              {t('设为待办', 'Make it a to-do')}
            </label>
            {todo && (
              <>
                <select aria-label={t('提醒时间', 'Remind at')} value={preset} onChange={(e) => setPreset(e.target.value)} style={field}>
                  <option value="tomorrow">{t('明天 10:00', 'Tomorrow 10:00')}</option>
                  <option value="2h">{t('2 小时后', 'In 2 hours')}</option>
                  <option value="48h">{t('48 小时后', 'In 48 hours')}</option>
                  <option value="tue">{t('下周二 10:00', 'Next Tue 10:00')}</option>
                  <option value="custom">{t('自选时间…', 'Pick a time…')}</option>
                </select>
                {preset === 'custom' && (
                  <>
                    <input type="datetime-local" aria-label={t('自选提醒时间', 'Reminder time')} value={custom}
                      min={toLocalInput(new Date())} onChange={(e) => setCustom(e.target.value)} style={field} />
                    <span style={{ fontSize: 11, fontWeight: 500, color: C.mute }}>{t('美西时间', 'PT')}</span>
                  </>
                )}
                <select aria-label={t('负责人', 'Assignee')} value={assignee || String(meId || '')} onChange={(e) => setAssignee(e.target.value)} style={field}>
                  {assignees.map((a) => <option key={a.id} value={String(a.id)}>{t('负责人：', 'Owner: ')}{a.name}</option>)}
                </select>
              </>
            )}
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={onPick} style={{ display: 'none' }} />
            <button type="button" aria-label={t('添加图片', 'Add images')} title={t('添加图片', 'Add images')} disabled={uploading}
              onClick={() => fileRef.current && fileRef.current.click()}
              style={{ width: 32, height: 30, border: `1px solid ${C.field}`, borderRadius: 2, background: '#FFFDF9', color: C.sub, cursor: uploading ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              {uploading ? '…' : <ImageIcon size={16} />}
            </button>
            {images.length < 9 && (
              <QrUploadButton profileId={profileId} isZh={isZh} variant="icon" target={{ kind: 'note' }}
                where={`${(profile && profile.first_name) || `#${profileId}`} · ${t('备注记录', 'Notes')} · ${t('图片', 'images')}`}
                onFiles={(urls) => appendCapped(setImages, urls, 9, isZh)} />
            )}
          </div>
          <button type="submit" disabled={saving}
            style={{ height: 32, padding: '0 16px', borderRadius: 2, border: 'none', background: C.brand, color: '#FFFDF9', font: 'inherit', fontSize: 13, fontWeight: 500, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? t('保存中…', 'Saving…') : t('保存', 'Save')}
          </button>
        </div>
      </form>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: C.sub }}>
          {rows === null ? t('加载中…', 'Loading…') : t(`共 ${list.length} 条 · 待办 ${openCount}`, `${list.length} entries · ${openCount} to-dos`)}
        </div>
        {authorOptions.length > 1 && (
          <label style={{ fontSize: 13, color: C.sub, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            {t('记录人', 'Written by')}
            <select value={author} onChange={(e) => setAuthor(e.target.value)} style={field}>
              <option value="">{t('全部老师', 'Everyone')}</option>
              {authorOptions.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </label>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {rows !== null && list.length === 0 && (
          <div style={{ padding: '1.6rem 1rem', textAlign: 'center', fontSize: 13, color: C.mute }}>
            {t('还没有客户情况记录。上面写下第一条；勾「设为待办」会到点提醒。', 'No notes yet. Write the first one above — tick 设为待办 to get a reminder.')}
          </div>
        )}
        {pg.slice.map((n) => {
          const tag = TAG[n._state];
          const isTodo = n._state === 'open' || n._state === 'overdue';
          const mine = meId != null && Number(n.created_by) === Number(meId);
          const meta = n._state === 'done'
            ? [n.done_by_name || n.created_by_name, fmtMD(n.done_at), n.done_result ? t(`结果：${n.done_result}`, `Result: ${n.done_result}`) : '']
            : n._state === 'note'
              ? [n.created_by_name, fmtMDHM(n.created_at)]
              : [
                n.created_by_name ? t(`${n.created_by_name} 写于 ${fmtMD(n.created_at)}`, `${n.created_by_name} · ${fmtMD(n.created_at)}`) : fmtMD(n.created_at),
                t(`提醒 ${fmtMDHM(n.remind_at)}`, `Remind ${fmtMDHM(n.remind_at)}`),
                n.assignee_name ? t(`负责人 ${n.assignee_name}`, `Owner ${n.assignee_name}`) : '',
              ];
          return (
            <div key={n.id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '11px 0', borderTop: `1px solid ${C.line}` }}>
              <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 2, background: tag.bg, color: tag.color, flexShrink: 0 }}>{isZh ? tag.zh : tag.en}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                {editing && editing.id === n.id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <textarea rows={2} value={editing.text} autoFocus onChange={(e) => setEditing((x) => ({ ...x, text: e.target.value }))}
                      style={{ ...field, fontSize: 14, resize: 'vertical' }} />
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button type="button" disabled={busyId === n.id} style={{ ...smallBtn, background: C.brand, color: '#FFFDF9', border: 'none' }}
                        onClick={async () => {
                          if (!editing.text.trim()) return;
                          const ok = await act(n.id, () => profilesAPI.updateProfileNote(profileId, n.id, editing.text.trim()), t('已更新', 'Updated'));
                          if (ok) setEditing(null);
                        }}>{t('保存', 'Save')}</button>
                      <button type="button" style={smallBtn} onClick={() => setEditing(null)}>{t('取消', 'Cancel')}</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 14, color: C.ink, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{n.note}</div>
                )}
                <div style={{ fontSize: 12.5, color: n._state === 'overdue' ? '#9E2B25' : C.sub, marginTop: 2 }}>
                  {meta.filter(Boolean).join(' · ')}
                  {mine && !editing && (
                    <>
                      <button type="button" style={{ ...linkBtn, color: C.brand, marginLeft: 6 }} onClick={() => setEditing({ id: n.id, text: n.note || '' })}>{t('编辑', 'Edit')}</button>
                      <button type="button" style={{ ...linkBtn, color: '#9E2B25' }} disabled={busyId === n.id}
                        onClick={() => {
                          // eslint-disable-next-line no-alert
                          if (!window.confirm(t('确定删除这条记录？删除后无法恢复。', 'Delete this note? This cannot be undone.'))) return;
                          act(n.id, () => profilesAPI.deleteProfileNote(profileId, n.id), t('已删除', 'Deleted'));
                        }}>{t('删除', 'Delete')}</button>
                    </>
                  )}
                  {n._state === 'done' && (
                    <button type="button" style={{ ...linkBtn, color: C.sub }} disabled={busyId === n.id}
                      onClick={() => act(n.id, () => profilesAPI.reopenNote(profileId, n.id), t('已重新打开', 'Reopened'))}>{t('重新打开', 'Reopen')}</button>
                  )}
                </div>
                {n._images.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                    {n._images.map((u, i) => {
                      const src = resolvePhotoUrl(typeof u === 'string' ? u : u && u.url) || (typeof u === 'string' ? u : u && u.url);
                      return src ? (
                        <a key={i} href={src} target="_blank" rel="noopener noreferrer" style={{ display: 'block', width: 48, height: 48, borderRadius: 2, overflow: 'hidden', border: `1px solid ${C.field}` }}>
                          <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </a>
                      ) : null;
                    })}
                  </div>
                )}
                {doneFor === n.id && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                    <input value={doneText} onChange={(e) => setDoneText(e.target.value)} maxLength={500} autoFocus
                      placeholder={t('结果（选填）', 'Result (optional)')} aria-label={t('完成结果', 'Result')}
                      style={{ ...field, flex: 1, minWidth: 160 }} />
                    <button type="button" disabled={busyId === n.id} onClick={() => markDone(n)}
                      style={{ ...smallBtn, background: C.ok, color: '#FFFDF9', border: 'none' }}>{t('确认完成', 'Mark done')}</button>
                    <button type="button" style={smallBtn} onClick={() => { setDoneFor(null); setDoneText(''); }}>{t('取消', 'Cancel')}</button>
                  </div>
                )}
              </div>
              {isTodo && doneFor !== n.id && (
                <button type="button" onClick={() => { setDoneFor(n.id); setDoneText(''); }}
                  style={{ ...smallBtn, color: C.ok }}>{t('完成', 'Done')}</button>
              )}
            </div>
          );
        })}
        <RecPager page={pg.page} pages={pg.pages} total={pg.total} onPage={setPage} isZh={isZh} />
      </div>
      <div style={{ fontSize: 12, color: C.mute }}>
        {t('不勾「设为待办」时，就是一条普通的客户情况记录。主管、经理、管理员能看到所有老师的记录。', 'Without 设为待办 it is an ordinary note. Supervisors, managers and admins see every coach’s notes.')}
      </div>
    </div>
  );
}
